import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHighlights, useBookmarks, useParagraphExplanations, HighlightableParagraph } from './HighlightableText';
import OverviewRuler from './OverviewRuler';
import './StudyGuide.css';

// Structural content — numbered steps (e.g. a worked example's solution) or a
// bullet list of facts — read much better broken onto their own lines than
// crammed into one run-on sentence. These aren't individually highlightable
// or bookmarkable (unlike HighlightableParagraph below); they're simple
// layout for content that's inherently a list, not prose.
function StructuredBlock({ block }) {
  return (
    <div className="study-structured-block">
      {block.label && (
        <span className={`study-paragraph-label study-paragraph-label--${block.kind || 'tip'}`}>{block.label}</span>
      )}
      {block.intro && <p className="study-paragraph study-structured-intro">{block.intro}</p>}
      {block.type === 'steps' && (
        <ol className="study-steps-list">
          {block.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}
      {block.type === 'list' && (
        <ul className="study-bullet-list">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DocumentReader({ documentId, title, intro, parts, tabbed = false }) {
  const { highlights, error: highlightsError, addHighlight, removeHighlight } = useHighlights(documentId);
  const { bookmarks, error: bookmarksError, addBookmark, removeBookmark } = useBookmarks(documentId);
  const explanations = useParagraphExplanations(documentId);
  const nodesRef = useRef({});
  const [activePartId, setActivePartId] = useState(parts[0]?.id);

  const registerNode = useCallback((paragraphId, node) => {
    if (node) {
      nodesRef.current[paragraphId] = node;
    } else {
      delete nodesRef.current[paragraphId];
    }
  }, []);

  const jumpToParagraph = useCallback((paragraphId) => {
    const node = nodesRef.current[paragraphId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const highlightsByParagraph = highlights.reduce((map, h) => {
    (map[h.paragraph_id] = map[h.paragraph_id] || []).push(h);
    return map;
  }, {});

  const bookmarksByParagraph = bookmarks.reduce((map, b) => {
    map[b.paragraph_id] = b;
    return map;
  }, {});

  const visibleParts = tabbed ? parts.filter((part) => part.id === activePartId) : parts;

  return (
    <div className="study-guide-page study-reader-page">
      <Link to="/study-guide" className="study-reader-back">&larr; Back to Study Guide</Link>
      <h1>{title}</h1>
      {intro && <p className="study-guide-intro">{intro}</p>}

      {tabbed && (
        <div className="study-tabs" role="tablist">
          {parts.map((part) => (
            <button
              key={part.id}
              type="button"
              role="tab"
              aria-selected={part.id === activePartId}
              className={`study-tab ${part.id === activePartId ? 'study-tab--active' : ''}`}
              onClick={() => setActivePartId(part.id)}
            >
              {part.title}
            </button>
          ))}
        </div>
      )}

      <p className="study-reader-hint">
        Select any text below to highlight it, or use the flag next to a paragraph to bookmark your place &mdash;
        both are saved to your account. Paragraphs with an AI explanation available show a star (✦) next to the
        flag &mdash; click it for a quick, plain-language explanation. The strip along the right edge shows where
        your highlights (gold) and bookmarks (navy) are in the document; click a mark to jump straight there.
      </p>
      {highlightsError && <p className="study-guide-error">{highlightsError}</p>}
      {bookmarksError && <p className="study-guide-error">{bookmarksError}</p>}

      <details className="study-reader-nav">
        <summary>Jump to section</summary>
        <ul>
          {visibleParts.flatMap((part) => part.sections).map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.heading}</a>
            </li>
          ))}
        </ul>
      </details>

      {visibleParts.map((part) => (
        <div key={part.id}>
          {part.title && !tabbed && <h2 className="study-part-title">{part.title}</h2>}
          {part.sections.map((section) => (
            <div className="study-section" id={section.id} key={section.id}>
              <h3>{section.heading}</h3>
              {section.sourceNote && <p className="study-section-source">{section.sourceNote}</p>}
              {section.paragraphs.map((para, idx) => {
                const paragraphId = `${section.id}-p${idx}`;

                if (para && (para.type === 'steps' || para.type === 'list')) {
                  return <StructuredBlock key={paragraphId} block={para} />;
                }

                const bookmark = bookmarksByParagraph[paragraphId];
                const text = typeof para === 'string' ? para : para.text;
                const label = typeof para === 'string' ? null : para.label;
                const kind = typeof para === 'string' ? null : para.kind;
                return (
                  <HighlightableParagraph
                    key={paragraphId}
                    paragraphId={paragraphId}
                    text={text}
                    label={label}
                    kind={kind}
                    highlights={highlightsByParagraph[paragraphId] || []}
                    onCreate={addHighlight}
                    onDelete={removeHighlight}
                    isBookmarked={Boolean(bookmark)}
                    onToggleBookmark={() => (bookmark ? removeBookmark(bookmark.id) : addBookmark(paragraphId))}
                    registerNode={registerNode}
                    paragraphExplanation={explanations[paragraphId] || null}
                  />
                );
              })}
            </div>
          ))}
        </div>
      ))}

      <OverviewRuler
        nodesRef={nodesRef}
        highlights={highlights}
        bookmarks={bookmarks}
        onJump={jumpToParagraph}
        refreshKey={activePartId}
      />
    </div>
  );
}
